/* eslint-disable import/no-named-as-default */
import React, { Component, ReactNode } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import TimeScaleDropDown from "./TimeScaleDropDown";
import AnalyticsWidgetLoading from "../../shared/loaders/AnalyticsWidgetLoading";
import applicationActionsTrackingTypes from "../types/applicationActionsTrackingTypes";
import {
  ChartTitle,
  ChartTitleText,
} from "../styles/employer-analytics-styles";
import {
  Wrapper,
  Chart,
  TimeToHireTotal,
  Days,
  TthTooltip,
  LineWrapper,
  LineSection,
  LineSectionDays,
  Tip,
  StartLabel,
  EndLabel,
} from "../styles/application-actions-tracking-styles";

interface IApplicationActionsTrackingProps {
  applicationActionsTracking: {
    timeToHireTotalDays: string;
    timeToHire: {
      applicationsReceived: {
        days: number;
        percent: number;
      };
      interviewProcess: {
        days: number;
        percent: number;
      };
      offerMade: {
        days: number;
        percent: number;
      };
      offerAccepted: {
        days: number;
        percent: number;
      };
    };
  };
  applicationActionsTrackingWidgetLoader: boolean;
  pdfExport?: boolean;
}

interface IApplicationActionsTrackingState {
  timescaleDropdownActive: boolean;
  activeTimescale: {
    id: number;
    name: string;
  };
}

export class ApplicationActionsTracking extends Component<
  IApplicationActionsTrackingProps,
  IApplicationActionsTrackingState
> {
  static propTypes = {
    applicationActionsTracking: applicationActionsTrackingTypes,
    applicationActionsTrackingWidgetLoader: PropTypes.bool.isRequired,
    pdfExport: PropTypes.bool,
  };

  static defaultProps = {
    pdfExport: null,
    applicationActionsTracking: {
      timeToHireTotalDays: "",
      timeToHire: {
        applicationsReceived: {
          days: 0,
          percent: 0,
        },
        interviewProcess: {
          days: 0,
          percent: 0,
        },
        offerMade: {
          days: 0,
          percent: 0,
        },
        offerAccepted: {
          days: 0,
          percent: 0,
        },
      },
    },
  };

  state = {
    timescaleDropdownActive: false,
    activeTimescale: {
      id: 0,
      name: "12 month view",
    },
  };

  toggleDropdown = (): void => {
    const { timescaleDropdownActive } = this.state;

    this.setState(() => ({
      timescaleDropdownActive: !timescaleDropdownActive,
    }));
  };

  selectTimescale = (timescale: { id: number; name: string }): void => {
    this.setState({
      activeTimescale: timescale,
    });
  };

  render(): ReactNode {
    const {
      applicationActionsTracking,
      applicationActionsTrackingWidgetLoader,
      pdfExport,
    } = this.props;

    const { timeToHire } = applicationActionsTracking;
    const { timescaleDropdownActive, activeTimescale } = this.state;

    const timescaleOptions = [
      { id: 0, name: "12 month view" },
      { id: 30, name: "Last 30 days" },
      { id: 90, name: "Last 90 days" },
    ];

    const interviewsTooltip =
      "To maximise your chances of success,<br> don’t leave more than <strong>5 days</strong> between<br> receiving applications and inviting<br> candidates to interview.<br><br> If possible, organise all interviews<br> for the same day.";
    const offerMadeTooltip =
      "To maximise your chances of success,<br> don’t leave more than <strong>2 days</strong> between<br> conducting interviews and making an offer.";
    const timeToHireTooltip =
      "This time to hire may be different to<br> one displayed at the top of this page,<br> as this widget only takes into account<br> jobs that have been through each<br> stage of the hiring process.";

    return (
      <Wrapper className="animated fadeIn delay-9" pdfExport={pdfExport}>
        <AnalyticsWidgetLoading
          text="Updating..."
          active={applicationActionsTrackingWidgetLoader}
        />
        <ReactTooltip
          html
          place="top"
          type="dark"
          effect="solid"
          aria-haspopup="true"
          id="aat-tooltip"
        />
        <ChartTitle data-test-id="aat-title" marginBottom={5}>
          <ChartTitleText>Recruitment Process Breakdown</ChartTitleText>
          {!pdfExport && (
            <TimeScaleDropDown
              toggleDropdown={this.toggleDropdown}
              dropdownActive={timescaleDropdownActive}
              selectTimescale={this.selectTimescale}
              activeTimescale={activeTimescale}
              resetFilterValue={{ id: 0, name: "12 month view" }}
              options={timescaleOptions}
              type="AAT"
              testId="aat-chart-dropdown"
            />
          )}
        </ChartTitle>
        <Chart data-test-id="aat-chart">
          <TimeToHireTotal data-test-id="avg-tth">
            Time to hire:
            <Days>
              {applicationActionsTracking.timeToHireTotalDays}
              {!pdfExport && (
                <TthTooltip
                  data-for="aat-tooltip"
                  data-tip={timeToHireTooltip}
                  data-place="bottom"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </TthTooltip>
              )}
            </Days>
          </TimeToHireTotal>
          <LineWrapper>
            <StartLabel>Job Posted</StartLabel>
            {/* applications received */}
            {timeToHire.applicationsReceived && (
              <LineSection
                width={timeToHire.applicationsReceived.percent}
                before="Applications Received"
              >
                <LineSectionDays pdfExport={pdfExport}>
                  {timeToHire.applicationsReceived.days}
                  {timeToHire.applicationsReceived.days === 1
                    ? " day"
                    : " days"}
                </LineSectionDays>
              </LineSection>
            )}
            {/* interview process */}
            {timeToHire.interviewProcess && (
              <LineSection
                width={timeToHire.interviewProcess.percent}
                before="Interview Process"
              >
                <LineSectionDays pdfExport={pdfExport}>
                  {timeToHire.interviewProcess.days}
                  {timeToHire.interviewProcess.days === 1 ? " day" : " days"}
                </LineSectionDays>
                {!pdfExport && (
                  <Tip data-for="aat-tooltip" data-tip={interviewsTooltip}>
                    Tip
                  </Tip>
                )}
              </LineSection>
            )}
            {/* offer made */}
            {timeToHire.offerMade && (
              <LineSection
                width={timeToHire.offerMade.percent}
                before="Offer Made"
              >
                <LineSectionDays pdfExport={pdfExport}>
                  {timeToHire.offerMade.days}
                  {timeToHire.offerMade.days === 1 ? " day" : " days"}
                </LineSectionDays>
                {!pdfExport && (
                  <Tip data-for="aat-tooltip" data-tip={offerMadeTooltip}>
                    Tip
                  </Tip>
                )}
              </LineSection>
            )}
            {/* offer accepted */}
            {timeToHire.offerAccepted && (
              <LineSection width={timeToHire.offerAccepted.percent}>
                <LineSectionDays pdfExport={pdfExport}>
                  {timeToHire.offerAccepted.days}
                  {timeToHire.offerAccepted.days === 1 ? " day" : " days"}
                </LineSectionDays>
              </LineSection>
            )}
            <EndLabel>Offer Accepted</EndLabel>
          </LineWrapper>
        </Chart>
      </Wrapper>
    );
  }
}

interface IReduxState {
  employerAnalytics: {
    chartsData: {
      applicationActionsTracking: unknown;
    };
    applicationActionsTrackingWidgetLoader: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  applicationActionsTracking:
    state.employerAnalytics.chartsData.applicationActionsTracking,
  applicationActionsTrackingWidgetLoader:
    state.employerAnalytics.applicationActionsTrackingWidgetLoader,
});

export default connect(mapStateToProps, {})(ApplicationActionsTracking);
